import type {
  ControlProvider,
  ControlProviderModel,
  ControlProviderStore,
} from "@/context/provider-types";
import type { ServerConnection } from "@/context/server";
import type {
  ProviderUsageOptions,
  ProviderUsageSummary,
} from "@/hooks/use-provider-usage";
import { accountMachineFetch } from "@/transport/account-machine";
import { controlApiFetch } from "@/transport/control-api";
import { localMode } from "@/transport/local-mode";
import {
  jsonArray,
  jsonBoolean,
  jsonNumber,
  jsonObject,
  jsonString,
} from "@/transport/decode";
import { expectJson, expectResponse } from "@/transport/http";
import { serverFetch } from "@/transport/server";

export type OpenCodeImportCandidate = {
  id: string;
  sourceProviderID: string;
  providerID?: string;
  name: string;
  label?: string;
  authType: string;
  importable: boolean;
  alreadyConnected: boolean;
  reason?: string;
};

export async function getOpenCodeProviderImports(
  server: ServerConnection.HttpBase,
) {
  const response = await serverFetch(server, "/api/provider-imports/opencode");
  const payload = await expectJson(response, (value) =>
    jsonObject(value, "Server returned invalid OpenCode imports"),
  );
  return {
    detected: Boolean(payload.detected),
    candidates: jsonArray(
      payload.candidates,
      "Server returned invalid OpenCode imports",
    ).map((raw) => {
      const value = jsonObject(raw, "Server returned invalid OpenCode import");
      return {
        id: jsonString(value.id, "Server returned invalid OpenCode import"),
        sourceProviderID: jsonString(
          value.sourceProviderID,
          "Server returned invalid OpenCode import",
        ),
        providerID:
          value.providerID === undefined
            ? undefined
            : jsonString(
                value.providerID,
                "Server returned invalid OpenCode import",
              ),
        name: jsonString(value.name, "Server returned invalid OpenCode import"),
        label:
          value.label === undefined
            ? undefined
            : jsonString(
                value.label,
                "Server returned invalid OpenCode import",
              ),
        authType: jsonString(
          value.authType,
          "Server returned invalid OpenCode import",
        ),
        importable: Boolean(value.importable),
        alreadyConnected: Boolean(value.alreadyConnected),
        reason:
          value.reason === undefined
            ? undefined
            : jsonString(
                value.reason,
                "Server returned invalid OpenCode import",
              ),
      } satisfies OpenCodeImportCandidate;
    }),
  };
}

export async function importOpenCodeProvider(
  server: ServerConnection.HttpBase,
  id: string,
  signal?: AbortSignal,
) {
  if (localMode()) {
    return expectResponse(await controlApiFetch("/api/local/provider-imports/opencode", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }), signal,
    }));
  }
  // The machine keeps the credential in its account, which shares it with
  // the account's other machines.
  const response = await serverFetch(server, "/api/provider-imports/opencode", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id }),
    signal,
  });
  return expectResponse(response);
}

function decodeProviderModel(value: unknown): ControlProviderModel {
  const model = jsonObject(value, "Server returned invalid provider model");
  return {
    ...model,
    id: jsonString(model.id, "Server returned invalid provider model"),
    name: jsonString(
      model.name ?? model.id,
      "Server returned invalid provider model",
    ),
    unlisted:
      model.unlisted === undefined || model.unlisted === null
        ? undefined
        : jsonBoolean(model.unlisted, "Server returned invalid provider model"),
    latest:
      model.latest === undefined || model.latest === null
        ? undefined
        : jsonBoolean(model.latest, "Server returned invalid provider model"),
  };
}

function decodeProvider(value: unknown): ControlProvider {
  const provider = jsonObject(value, "Server returned invalid provider");
  const models =
    provider.models === undefined
      ? {}
      : jsonObject(provider.models, "Server returned invalid provider models");
  return {
    ...provider,
    id: jsonString(provider.id, "Server returned invalid provider"),
    name: jsonString(
      provider.name ?? provider.id,
      "Server returned invalid provider",
    ),
    source:
      provider.source === undefined ? undefined : jsonString(provider.source),
    env:
      provider.env === undefined
        ? undefined
        : jsonArray(provider.env).map((item) => jsonString(item)),
    options:
      provider.options === undefined ? undefined : jsonObject(provider.options),
    models: Object.fromEntries(
      Object.entries(models).map(([id, model]) => [
        id,
        decodeProviderModel(model),
      ]),
    ),
  };
}

export function decodeProviderStore(value: unknown): ControlProviderStore {
  const store = jsonObject(value, "Server returned invalid providers");
  const defaults = jsonObject(
    store.default,
    "Server returned invalid providers",
  );
  return {
    all: jsonArray(store.all, "Server returned invalid providers").map(
      decodeProvider,
    ),
    connected: jsonArray(
      store.connected,
      "Server returned invalid providers",
    ).map((id) => jsonString(id)),
    default: Object.fromEntries(
      Object.entries(defaults).map(([id, model]) => [id, jsonString(model)]),
    ),
    version:
      store.version === undefined ? undefined : jsonNumber(store.version),
  };
}

function decodeProviderUsage(
  value: unknown,
): Record<string, ProviderUsageOptions> {
  const body = jsonObject(value, "Server returned invalid provider usage");
  const providers =
    body.providers === undefined
      ? {}
      : jsonObject(body.providers, "Server returned invalid provider usage");
  const decoded: Record<string, ProviderUsageOptions> = {};
  const optionalString = (value: unknown) =>
    value === undefined ? undefined : jsonString(value, "Server returned invalid provider usage");
  const optionalNumber = (value: unknown) =>
    value === undefined ? undefined : jsonNumber(value, "Server returned invalid provider usage");
  for (const [providerID, value] of Object.entries(providers)) {
    const snapshot = jsonObject(value, "Server returned invalid provider usage");
    const rawWindows = jsonArray(snapshot.windows, "Server returned invalid provider usage");
    const windows: ProviderUsageSummary[] = rawWindows.map((value) => {
      const window = jsonObject(value, "Server returned invalid provider usage");
      return {
        id: optionalString(window.id),
        label: jsonString(window.label, "Server returned invalid provider usage"),
        used_percent: jsonNumber(window.used_percent, "Server returned invalid provider usage"),
        reset_at: optionalNumber(window.reset_at),
        limit_window_seconds: optionalNumber(window.limit_window_seconds),
      };
    });
    decoded[providerID] = {
      windows,
      usage: windows[0],
      plan: optionalString(snapshot.plan),
      source: optionalString(snapshot.source),
      machine: optionalString(snapshot.machine),
      checkedAt: optionalNumber(snapshot.checkedAt),
      stale:
        snapshot.stale === undefined
          ? undefined
          : jsonBoolean(snapshot.stale, "Server returned invalid provider usage"),
    };
  }
  return decoded;
}

export function getControlProviders(signal?: AbortSignal) {
  return accountMachineFetch("/api/providers", { signal }).then((response) =>
    expectJson(response, decodeProviderStore),
  );
}

export function getProviderUsage() {
  return controlApiFetch("/api/providers/usage").then((response) =>
    expectJson(response, decodeProviderUsage),
  );
}

export type ProviderModelRefreshResult = {
  providerID: string;
  changed: boolean;
  count: number;
  checkedAt?: number;
  added: string[];
  removed: string[];
  updated: string[];
  pending: string[];
};

function decodeProviderModelRefresh(value: unknown): ProviderModelRefreshResult {
  const payload = jsonObject(value, "Provider model refresh returned invalid data");
  const result = jsonObject(
    payload.result,
    "Provider model refresh returned invalid data",
  );
  const strings = (value: unknown) =>
    jsonArray(value ?? [], "Provider model refresh returned invalid data").map(
      (item) =>
        jsonString(item, "Provider model refresh returned invalid data"),
    );
  return {
    providerID: jsonString(
      result.providerID,
      "Provider model refresh returned invalid data",
    ),
    changed: jsonBoolean(
      result.changed,
      "Provider model refresh returned invalid data",
    ),
    count: jsonNumber(
      result.count,
      "Provider model refresh returned invalid data",
    ),
    checkedAt:
      result.checkedAt === undefined
        ? undefined
        : jsonNumber(
            result.checkedAt,
            "Provider model refresh returned invalid data",
          ),
    added: strings(result.added),
    removed: strings(result.removed),
    updated: strings(result.updated),
    pending: strings(result.pending),
  };
}

export async function refreshProviderModels(providerID: string) {
  return expectJson(
    await accountMachineFetch(
      `/api/providers/${encodeURIComponent(providerID)}/models/refresh`,
      { method: "POST" },
    ),
    decodeProviderModelRefresh,
    { error: "Provider model refresh failed" },
  );
}

// replace targets the given instance instead of allocating a new one, which is
// what re-entering a key for an already connected account must do.
export async function connectProvider(
  providerID: string,
  key: string,
  replace = false,
) {
  await putProviderCredential(providerID, { type: "api", key }, replace);
}

/**
 * An OpenAI-compatible endpoint: its base URL, its key (empty for servers that
 * take none), and the limits Mux uses for models its listing does not describe.
 */
export type EndpointConnection = {
  baseURL: string;
  key: string;
  contextLimit: number;
  outputLimit: number;
};

/** Connects an endpoint and returns the provider ID the account gave it. */
export async function connectEndpoint(
  providerID: string,
  connection: EndpointConnection,
  replace = false,
) {
  const response = await putProviderCredential(providerID, { type: "api", ...connection }, replace);
  return expectJson(response, (value) =>
    jsonString(jsonObject(value, "Connect returned invalid data").providerID, "Connect returned no provider ID"),
  );
}

async function putProviderCredential(
  providerID: string,
  credential: Record<string, unknown>,
  replace: boolean,
) {
  const path = `/auth/${encodeURIComponent(providerID)}${replace ? "?replace=true" : ""}`;
  return expectResponse(
    await accountMachineFetch(path, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credential),
    }),
    { error: "Could not connect provider" },
  );
}

/** A connected provider's saved API key, read only to copy it. */
export async function readProviderAPIKey(providerID: string) {
  return expectJson(
    await accountMachineFetch(`/auth/${encodeURIComponent(providerID)}/key`, {
      cache: "no-store",
    }),
    (value) =>
      jsonString(
        jsonObject(value, "API key response was invalid").key,
        "No API key was returned",
      ),
    { error: "Could not read API key" },
  );
}

export async function disconnectProvider(providerID: string) {
  await expectResponse(
    await accountMachineFetch(`/auth/${encodeURIComponent(providerID)}`, {
      method: "DELETE",
    }),
    {
      error: "Could not disconnect provider",
    },
  );
}

export type OAuthAuthorizationResponse = {
  url: string;
  method?: string;
  instructions?: string;
  sessionID: string;
  pollIntervalMs: number;
};

export async function authorizeProviderOAuth(
  providerID: string,
  method: number,
  reconnect: boolean,
) {
  return expectJson(
    await accountMachineFetch(
      `/provider/${encodeURIComponent(providerID)}/oauth/authorize`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ method, reconnect }),
      },
    ),
    (value): OAuthAuthorizationResponse => {
      const result = jsonObject(
        value,
        "OAuth authorization returned invalid data",
      );
      const sessionID = result.sessionID ?? result.sessionId;
      const interval = result.interval_ms ?? result.intervalMs ?? 5_000;
      return {
        url: jsonString(
          result.url,
          "OAuth authorization did not return a verification URL",
        ),
        method:
          result.method === undefined ? undefined : jsonString(result.method),
        instructions:
          result.instructions === undefined
            ? undefined
            : jsonString(result.instructions),
        sessionID: jsonString(
          sessionID,
          "OAuth authorization did not return a session",
        ),
        pollIntervalMs: Math.max(5_000, jsonNumber(interval)),
      };
    },
  );
}

export async function pollProviderOAuth(providerID: string, body: unknown) {
  const response = await accountMachineFetch(
    `/provider/${encodeURIComponent(providerID)}/oauth/callback`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (response.status !== 202) {
    await expectResponse(response);
    return { pending: false as const };
  }
  const retryAfter = await expectJson(
    response,
    (value) => {
      const pending = jsonObject(value, "OAuth polling returned invalid data");
      return pending.retry_after_ms === undefined
        ? undefined
        : jsonNumber(pending.retry_after_ms);
    },
    { allow: [202] },
  );
  return { pending: true as const, retryAfter };
}

export async function completeProviderOAuth(providerID: string, body: unknown) {
  await expectResponse(
    await accountMachineFetch(
      `/provider/${encodeURIComponent(providerID)}/oauth/callback`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    ),
    { error: "Provider authorization failed" },
  );
}
